import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { delay } from 'lodash';
import { requestEmailSignature, createPostmarkSignature, resendPostmarkConfirmation } from './actions';
import { getEmailSignature } from './reducer';
import Timer from '../timer';
import Spinner from '../spinner';
import { getUser } from '../../modules/Auth/AuthReducer';


class RequestSignatureForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loaded: false,
      resending: false,
      resendingMessage: null,
      resendingErrors: null,
      resendingErrorCode: null,
    };
    this.createSignature = this.createSignature.bind(this);
    this.acceptResendError = this.acceptResendError.bind(this);
    this.requestReloadSignature = this.requestReloadSignature.bind(this);
    this.requestResendConfirmationEmail = this.requestResendConfirmationEmail.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  createSignature() {
    this.setState({ loading: true }, () => {
      this.props.showLoading(true);
      this.props.dispatch(createPostmarkSignature(this.props.user.email))
        .then(
          () => {
            this.props.showLoading(false);
            this.setState({ loading: false, loaded: true });
          },
          ({ data: { message } }) => {
            const el = (<div>{message}</div>);
            this.context.addNotification(el, { type: 'error', autoClose: false });
            this.setState({ loading: false, loaded: true });
            this.props.showLoading(false);
          },
        );
    });
  }

  acceptResendError(e) {
    e.preventDefault();
    this.setState({ resendingErrors: null, resendingErrorCode: null });
  }

  requestReloadSignature() {
    this.loadSignature(this.props.user.email, true);
  }

  requestResendConfirmationEmail(e) {
    e.preventDefault();
    this.setState({ resending: true, resendingErrors: null, resendingErrorCode: null, resendingMessage: null }, () => {
      this.props.dispatch(resendPostmarkConfirmation())
        .then(
          ({ message }) => {
            this.setState({ resending: false, resendingMessage: message });
            delay(() => {
              if (this.mounted) {
                this.setState({ resendingMessage: null });
              }
            }, 3000);
          },
          (errors) => {
            let message = null;
            let code = null;
            if (errors && errors.data && errors.data.message) {
              ({ message, code } = errors.data);
            }
            this.setState({ resending: false, resendingErrors: message, resendingErrorCode: code });
          },
        );
    });
  }

  loadSignature(email, silent = false) {
    this.props.showLoading(true);
    this.props.dispatch(requestEmailSignature(email, silent))
      .then(
        () => {
          this.props.showLoading(false);
        },
        () => {
          this.props.showLoading(false);
        },
      );
  }

  renderRequestCreateSignature() {
    const { user, onCancel } = this.props;
    return (
      <div>
        <div className="alert alert-warning">
          <p>
            In order to use the feedback request functionality you need to authorize TruVoice and TruVoice Automation to send emails on your behalf.
          </p>
          <p>
            The emails sent to your contacts, asking for their feedback, will come from your email address and any replies to those emails will come back to you.
          </p>
          <p>
            Your email address will be used in the from email field and reply-to email fields:
          </p>
        </div>
        <div className="well well-sm">
          <table className="table table-condensed" style={{ margin: 0 }}>
            <tbody>
              <tr>
                <td className="text-muted">From</td>
                <td><strong>{user.display_name}</strong> <code>&lt;{user.email}&gt;</code></td>
              </tr>
              <tr>
                <td className="text-muted">Reply-to</td>
                <td><code>{user.email}</code></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Click the following button to receive the email to authorize TruVoice and TruVoice Automation to send email on your behalf:
        </p>
        <div className="text-center">
          <Button bsStyle="primary" bsSize="lg" onClick={this.createSignature}>
            Send Authorization Email <sup>*</sup>
          </Button>
        </div>
        <p className="text-center">
          <small>* You will receive a confirmation email from <a href="https://postmarkapp.com" target="_blank">PostmarkApp</a></small>
        </p>
        {onCancel !== null &&
          <div className="text-center"><Button onClick={onCancel} bsStyle="link">Cancel</Button></div>
        }
      </div>
    );
  }

  renderResendConfirmationEmailErrors() {
    const { resendingErrors, resendingErrorCode } = this.state;
    let message = resendingErrors;
    const email = process.env.REACT_APP_EMAIL_SUPPORT;
    if (resendingErrorCode === 'CANNOT_RESEND_EMAIL_SIGNATURE') {
      message = (
        <div>
          <p><strong>Uh oh! It looks like you have not authorized TruVoice Automation to send emails on your behalf.</strong></p>
          This step is required. Please contact support at <a style={{ textDecoration: 'underline' }} href={`mailto:${email}`}>{email}</a>.<br />
          We will help you get this last authorization step completed<br /> so you are able to submit feedback requests for your opportunities.
        </div>
      );
    }
    return (
      <div className="text-center">
        <div className="alert alert-danger">
          {message}
        </div>
        <div>
          <Button bsStyle="primary" onClick={this.acceptResendError}>
            OK
          </Button>
        </div>
        <p />
      </div>
    );
  }

  renderResendConfirmationEmailSuccess() {
    const { resendingMessage } = this.state;
    return (
      <div className="text-center">
        <div className="alert alert-success">{resendingMessage}</div>
        <p />
      </div>
    );
  }
  renderRequestResendConfirmEmail() {
    const { user } = this.props;
    const { resendingMessage, resendingErrors } = this.state;
    if (resendingErrors !== null) {
      return this.renderResendConfirmationEmailErrors();
    }
    if (resendingMessage !== null) {
      return this.renderResendConfirmationEmailSuccess();
    }
    return (
      <div className="text-center">
        <Timer
          timeout={5000}
          enabled
          callback={this.requestReloadSignature}
        />
        <p>Please check your confirmation email at <code>{user.email}</code> and follow the instructions.</p>
        <p><a href="" onClick={this.requestResendConfirmationEmail}>Click here</a> if you did not receive any confirmation email.</p>
      </div>
    );
  }

  renderContent() {
    const { sent } = this.props;
    if (sent) {
      return this.renderRequestResendConfirmEmail();
    }
    return this.renderRequestCreateSignature();
  }

  render() {
    const { loading, resending } = this.state;
    const isLoading = loading || resending;
    if (isLoading) {
      return (<div className="text-center"><Spinner /><p /><p /></div>);
    }
    return this.renderContent();
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  const user = getUser(state);
  const info = getEmailSignature(state, user.email);
  return {
    verified: info.signature === 'verified',
    sent: info.signature === 'sent',
    user,
  };
}

RequestSignatureForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  sent: PropTypes.bool,
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
  }).isRequired,
  showLoading: PropTypes.func,
  onCancel: PropTypes.func,
};
RequestSignatureForm.defaultProps = {
  sent: false,
  showLoading: () => {},
  onCancel: null,
};
RequestSignatureForm.contextTypes = {
  addNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(RequestSignatureForm);
